// src/hooks/useAdminQuotes.js
import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

const useAdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const convertTimestamp = (timestamp) => {
      if (!timestamp) return null;
      return timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    };

    const formatQuoteData = (doc, collectionType = 'quotes') => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        customerName: data.customerName || data.name || 'Unknown Customer',
        customerEmail: data.customerEmail || data.email || '',
        customerPhone: data.customerPhone || data.phone || '',
        service: data.service || {
          name: data.serviceRequested || 'General Inquiry',
          category: data.serviceCategory || 'general',
          description: data.message || data.description || ''
        },
        sourceCollection: collectionType,
        requestedAt: convertTimestamp(data.requestedAt) || convertTimestamp(data.createdAt),
        reviewedAt: convertTimestamp(data.reviewedAt),
        approvedAt: convertTimestamp(data.approvedAt),
        rejectedAt: convertTimestamp(data.rejectedAt),
        acceptedAt: convertTimestamp(data.acceptedAt),
        completedAt: convertTimestamp(data.completedAt),
        updatedAt: convertTimestamp(data.updatedAt),
        createdAt: convertTimestamp(data.createdAt)
      };
    };

    const loadQuotes = async () => {
      try {
        setLoading(true);
        setError(null);

        const [quotesSnapshot] = await Promise.all([
          getDocs(query(collection(db, 'quotes'), orderBy('createdAt', 'desc')))
        ]);

        const allQuotes = [];
        quotesSnapshot.forEach((doc) => {
          allQuotes.push(formatQuoteData(doc, 'quotes'));
        });

        allQuotes.sort((a, b) => {
          const dateA = a.createdAt || new Date(0);
          const dateB = b.createdAt || new Date(0);
          return dateB - dateA;
        });

        setQuotes(allQuotes);
      } catch (err) {
        setError('Failed to load quotes');
        console.error('Error loading quotes:', err);
      } finally {
        setLoading(false);
      }
    };

    loadQuotes();
  }, []);

  const updateQuote = async (quoteId, updates) => {
    try {
      setLoading(true);
      setError(null);

      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) throw new Error('Quote not found');

      const collectionName = quote.sourceCollection || 'quotes';
      await updateDoc(doc(db, collectionName, quoteId), {
        ...updates,
        updatedAt: serverTimestamp()
      });

      setQuotes(prevQuotes =>
        prevQuotes.map(q =>
          q.id === quoteId ? { ...q, ...updates, updatedAt: new Date() } : q
        )
      );

      return { success: true };
    } catch (err) {
      setError('Failed to update quote');
      console.error('Error updating quote:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteQuote = async (quoteId) => {
    try {
      setLoading(true);
      setError(null);

      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) throw new Error('Quote not found');

      const collectionName = quote.sourceCollection || 'quotes';
      await deleteDoc(doc(db, collectionName, quoteId));

      setQuotes(prevQuotes => prevQuotes.filter(q => q.id !== quoteId));
      return { success: true };
    } catch (err) {
      setError('Failed to delete quote');
      console.error('Error deleting quote:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getQuoteStats = () => {
    const now = new Date();
    const twentyFourHoursAgo = new Date(now - 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    const newQuotes = quotes.filter(q => q.status === 'new').length;
    const pendingQuotes = quotes.filter(q => q.status === 'pending').length;

    return {
      totalQuotes: quotes.length,
      pendingQuotes: pendingQuotes + newQuotes, // used by AdminDashboard
      last24Hours: quotes.filter(q => q.createdAt && q.createdAt >= twentyFourHoursAgo).length,
      lastWeek: quotes.filter(q => q.createdAt && q.createdAt >= sevenDaysAgo).length,
      lastMonth: quotes.filter(q => q.createdAt && q.createdAt >= thirtyDaysAgo).length,
    };
  };

  const filterQuotes = (searchTerm, statusFilter = 'all', sourceFilter = 'all') => {
    return quotes.filter(quote => {
      if (statusFilter !== 'all' && quote.status !== statusFilter) return false;

      if (sourceFilter !== 'all') {
        if (sourceFilter === 'quotes' && quote.sourceCollection !== 'quotes') return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const customerName = (quote.customerName || '').toLowerCase();
        const serviceName = (quote.service?.name || quote.serviceRequested || '').toLowerCase();
        const email = (quote.customerEmail || '').toLowerCase();
        if (!customerName.includes(term) && !serviceName.includes(term) && !email.includes(term)) {
          return false;
        }
      }

      return true;
    });
  };

  return {
    quotes,
    loading,
    error,
    setError,
    updateQuote,
    deleteQuote,
    getQuoteStats,
    filterQuotes
  };
};

export default useAdminQuotes;